// Web Audio LoFi radio engine.
// Playback is driven by JSON-authored charts (no bundled audio files).

import { LOFI_CHARTS } from '@/lofi/charts';

function hashStringToSeed(str) {
  // Small, deterministic hash for seeding RNG.
  let h = 2166136261;
  for (let i = 0; i < String(str).length; i += 1) {
    h ^= String(str).charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function pitchClassFromNoteName(note) {
  const match = /^\s*([A-Ga-g])([#b]?)/.exec(String(note || ''));
  if (!match) return null;
  const letter = match[1].toUpperCase();
  const acc = match[2] || '';
  const base = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }[letter];
  if (typeof base !== 'number') return null;
  const delta = acc === '#' ? 1 : (acc === 'b' ? -1 : 0);
  return (base + delta + 12) % 12;
}

function noteNameToMidi(note, defaultOctave = 4) {
  const match = /^\s*([A-Ga-g])([#b]?)(-?\d+)?\s*$/.exec(String(note || ''));
  if (!match) return null;
  const pc = pitchClassFromNoteName(match[1] + (match[2] || ''));
  if (pc == null) return null;
  const octave = match[3] == null ? defaultOctave : Number(match[3]);
  if (!Number.isFinite(octave)) return null;
  // MIDI: C-1 is 0, so octave + 1.
  return (octave + 1) * 12 + pc;
}

function parseChordSymbol(symbol) {
  const match = /^\s*([A-Ga-g])([#b]?)(.*)\s*$/.exec(String(symbol || ''));
  if (!match) return null;
  const rootPc = pitchClassFromNoteName(match[1] + (match[2] || ''));
  if (rootPc == null) return null;

  const suffix = (match[3] || '').trim();
  const normalized = suffix.replace(/\s+/g, '');

  // Special-case 6/9 notation.
  if (/^6\/9$/i.test(normalized) || /^69$/i.test(normalized)) {
    return { rootPc, quality: 'maj69' };
  }

  const isMaj = /^maj/i.test(normalized);
  const isMin = !isMaj && /^m(?!aj)/i.test(normalized);
  const extMatch = /(\d+)/.exec(normalized);
  const ext = extMatch ? Number(extMatch[1]) : null;

  let quality = 'dom7';
  if (isMin) {
    if (ext && ext >= 11) quality = 'min11';
    else quality = (ext && ext >= 9) ? 'min9' : 'min7';
  }
  else if (isMaj) quality = (ext && ext >= 9) ? 'maj9' : 'maj7';
  else if (ext && ext >= 13) quality = 'dom13';
  else if (ext && ext >= 9) quality = 'dom9';
  else quality = 'dom7';

  return { rootPc, quality };
}

function nearestMidiForPitchClass(anchorMidi, targetPc) {
  const anchorPc = ((anchorMidi % 12) + 12) % 12;
  const raw = (targetPc - anchorPc + 12) % 12;
  const delta = raw <= 6 ? raw : raw - 12;
  return anchorMidi + delta;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return function rand() {
    a |= 0;
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick(rng, arr) {
  return arr[Math.floor(rng() * arr.length)];
}

function midiToHz(midi) {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

function createImpulseResponse(context, durationSec, decay, rng) {
  const sampleRate = context.sampleRate;
  const length = Math.max(1, Math.floor(sampleRate * durationSec));
  const impulse = context.createBuffer(2, length, sampleRate);

  for (let channel = 0; channel < 2; channel += 1) {
    const data = impulse.getChannelData(channel);
    for (let i = 0; i < length; i += 1) {
      const t = i / length;
      const amp = Math.pow(1 - t, decay);
      data[i] = (rng() * 2 - 1) * amp;
    }
  }

  return impulse;
}

function createNoiseBuffer(context, durationSec, rng) {
  const sampleRate = context.sampleRate;
  const length = Math.max(1, Math.floor(sampleRate * durationSec));
  const buffer = context.createBuffer(1, length, sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i += 1) {
    // Slightly pink-ish by averaging white noise.
    const white = rng() * 2 - 1;
    const white2 = rng() * 2 - 1;
    data[i] = (white + white2) * 0.5;
  }
  return buffer;
}

function scaleStepsForMode(mode) {
  // Semitone degrees from root.
  switch (mode) {
    case 'major':
      return [0, 2, 4, 5, 7, 9, 11];
    case 'dorian':
      return [0, 2, 3, 5, 7, 9, 10];
    case 'harmonicMinor':
      return [0, 2, 3, 5, 7, 8, 11];
    case 'naturalMinor':
    default:
      return [0, 2, 3, 5, 7, 8, 10];
  }
}

function chooseMelodyRhythm(r) {
  // 16th-note positions within a bar (0..15). Designed to feel "lofi hiphop" and not too busy.
  return pick(r, [
    [2, 6, 10, 14],
    [1, 7, 9, 15],
    [3, 6, 11, 14],
    [2, 5, 10, 13],
    [4, 7, 12],
    [6, 10, 14]
  ]);
}

function chooseMotifDegrees(scale, r) {
  // Degrees in semitones relative to key root.
  // Prefer consonant scale degrees, with occasional color tone.
  const safe = scale.slice();
  const color = [2, 5, 9, 10, 11].filter((x) => safe.includes(x));
  const len = pick(r, [4, 4, 5, 6]);
  const motif = [];
  for (let i = 0; i < len; i += 1) {
    const useColor = i > 0 && r() < 0.18 && color.length;
    motif.push(useColor ? pick(r, color) : pick(r, safe));
  }
  return motif;
}

const CHART_SONGS = Object.values(LOFI_CHARTS).map((chart) => ({
  id: chart.id,
  name: chart.name,
  type: 'chart',
  chartId: chart.id,
  seed: hashStringToSeed(chart.id),
  bpm: chart.bpm,
  key: chart.key,
  lengthBars: chart.bars
}));

// Songs list (JSON chart-authored only).
export const LOFI_SONGS = CHART_SONGS;

export function createLofiRadioEngine({ song, onStateChange } = {}) {
  const playlist = LOFI_SONGS.slice();
  let songIndex = Math.max(0, playlist.findIndex((s) => song && s.id === song.id));
  if (songIndex === -1) songIndex = 0;

  let currentSong = playlist[songIndex];
  let currentChart = (currentSong && currentSong.type === 'chart')
    ? (LOFI_CHARTS[(currentSong && (currentSong.chartId || currentSong.id))] || null)
    : null;
  let chartPlayback = null;
  let rng = mulberry32((currentSong && currentSong.seed) || 1);

  let profile = null;

  let context = null;
  let master = null;

  // Nodes we may tweak per song.
  let toneNode = null;
  let muffleShelfNode = null;
  let convolverNode = null;
  let noiseGainNode = null;
  let wetGainNode = null;
  let saturationNode = null;
  let wetWidthDelayNode = null;

  let started = false;
  let schedulerId = null;
  let uiTickId = null;

  let nextNoteTime = 0;
  let step = 0;

  // Transport
  let startTimeSec = 0;
  let transportOffsetSec = 0;
  let lastAutoAdvanceAt = -1;

  const melodyMotifCache = new WeakMap();

  const state = {
    playing: false,
    songName: currentSong ? currentSong.name : 'LoFi Radio',
    bpm: currentSong && currentSong.bpm ? currentSong.bpm : 74,
    key: currentSong && currentSong.key ? currentSong.key : 'A minor',
    volume: 0.25,
    songIndex,
    playlistLength: playlist.length,
    durationSec: 0,
    positionSec: 0
  };

  function emit() {
    if (typeof onStateChange === 'function') onStateChange({ ...state });
  }

  function songSeed(songObj) {
    return ((songObj && songObj.seed) || 1) >>> 0;
  }

  function profileRng(songObj, salt) {
    return mulberry32((songSeed(songObj) ^ salt) >>> 0);
  }

  function uniqueProgressionSequence(songObj, bars, r) {
    const mode = (songObj && songObj.mode) || 'naturalMinor';
    const scaleSteps = scaleStepsForMode(mode);
    const fixedOptions = [
      [0, 5, 7, 3],
      [0, 3, 5, 7],
      [0, 7, 5, 3],
      [0, 10, 7, 3],
      [0, 8, 5, 7],
      [0, 5, 3, 10],
      [0, 7, 10, 5],
      [0, 2, 7, 5]
    ];

    const chunks = Math.max(1, Math.ceil(bars / 4));
    const seq = [];
    let last = null;
    for (let i = 0; i < chunks; i += 1) {
      let next = pick(r, fixedOptions);
      // Ensure we don't repeat the same chunk progression too often.
      if (last && next.join(',') === last.join(',')) {
        next = pick(r, fixedOptions);
      }

      // Nudge into minor scale if needed.
      const cleaned = next.map((d) => {
        if (scaleSteps.includes(d)) return d;
        // Find nearest scale tone.
        let best = scaleSteps[0];
        let bestDist = 999;
        for (let k = 0; k < scaleSteps.length; k += 1) {
          const dist = Math.abs(scaleSteps[k] - d);
          if (dist < bestDist) {
            bestDist = dist;
            best = scaleSteps[k];
          }
        }
        return best;
      });

      seq.push(cleaned);
      last = cleaned;
    }
    return seq;
  }

  function chordQualityForDegree(degreeSemitones, r) {
    // Jazzy, calm flavors.
    if (degreeSemitones === 7) return pick(r, ['dom9', 'dom7']); // V
    if (degreeSemitones === 10) return pick(r, ['maj7', 'maj9']); // bVII
    if (degreeSemitones === 5) return pick(r, ['min7', 'min9']); // iv
    return pick(r, ['min7', 'min9']); // i, III, VI etc.
  }

  function chordIntervals(quality) {
    switch (quality) {
      case 'min9':
        return [0, 3, 7, 10, 14];
      case 'min11':
        return [0, 3, 7, 10, 14, 17];
      case 'maj7':
        return [0, 4, 7, 11];
      case 'maj9':
        return [0, 4, 7, 11, 14];
      case 'maj69':
        return [0, 4, 7, 9, 14];
      case 'dom7':
        return [0, 4, 7, 10];
      case 'dom9':
        return [0, 4, 7, 10, 14];
      case 'dom13':
        return [0, 4, 7, 10, 14, 21];
      case 'min7':
      default:
        return [0, 3, 7, 10];
    }
  }

  function createSongProfile(songObj) {
    const r = profileRng(songObj, 0xC0FFEE);
    const bars = (songObj && songObj.lengthBars) || 64;

    const mode = pick(r, ['naturalMinor', 'naturalMinor', 'dorian', 'harmonicMinor']);
    const arrangement = pick(r, ['pad', 'pad+keys', 'keys', 'pad+pluck', 'pad+keys+melody']);
    const drumFeel = pick(r, ['standard', 'standard', 'halftime', 'breakbeat']);
    const drumKit = pick(r, ['soft', 'soft', 'dusty', 'crisp']);

    const swing = 0.04 + r() * 0.06; // portion of 16th note
    const hatMode = pick(r, ['eighth', 'eighth', 'sixteenth', 'sparse']);
    const hatOpenChance = 0.04 + r() * 0.10;

    const kickPatterns = [
      [0, 8],
      [0, 7, 8],
      [0, 6, 8, 14],
      [0, 8, 10],
      [0, 5, 8, 13],
      [0, 8, 15]
    ];
    const kickSteps = pick(r, kickPatterns);

    const snareGhostSteps = pick(r, [[14], [15], [13, 15], []]);
    const snareGhostChance = 0.18 + r() * 0.35;

    const chordWave = pick(r, ['triangle', 'sine']);
    const chordDetuneCents = 2 + r() * 10;
    const chordLPBase = 900 + r() * 900;
    const chordLPRng = 800 + r() * 1400;
    const chordGainBase = 0.10 + r() * 0.08;

    const chordRhythm = pick(r, ['pad', 'pad', 'arp', 'chop']);
    const arpDensity = pick(r, [2, 3, 4]);

    const bassStyle = pick(r, ['root-approach', 'root-fifth', 'walking']);

    const keysTimbre = pick(r, ['rhodes', 'rhodes', 'tine', 'bell']);
    const pluckDamp = 0.25 + r() * 0.5;
    // Every song gets a melody; keep it calm.
    const melodyDensity = 0.22 + r() * 0.28;
    const melodyRegister = pick(r, [12, 24]);
    const melodyInstrument = pick(r, ['keys', 'keys', 'pluck', 'softLead']);

    const toneCutoff = 10500 + r() * 4500;
    const vinylLevel = 0.008 + r() * 0.010;
    const reverbWet = 0.12 + r() * 0.12;
    const bassCutoffHz = 200 + r() * 90;
    const tapeSaturation = 0;
    const stereoWidth = 0;

    const crackleChancePerBar = 0.08 + r() * 0.18;
    const wobbleDetuneCents = 0.5 + r() * 2.5;
    const wobbleRateHz = 0.07 + r() * 0.18;

    const progressionSequence = uniqueProgressionSequence({ ...songObj, mode }, bars, profileRng(songObj, 0xBADC0DE));

    const scale = scaleStepsForMode(mode);
    const melodyRhythm = chooseMelodyRhythm(profileRng(songObj, 0xCAFE01));
    const melodyMotif = chooseMotifDegrees(scale, profileRng(songObj, 0xCAFE02));
    const motifShiftChance = 0.10 + r() * 0.22;

    return {
      bars,
      mode,
      arrangement,
      drumFeel,
      drumKit,
      swing,
      hatMode,
      hatOpenChance,
      kickSteps,
      snareGhostSteps,
      snareGhostChance,
      chordWave,
      chordDetuneCents,
      chordLPBase,
      chordLPRng,
      chordGainBase,
      chordRhythm,
      arpDensity,
      bassStyle,
      keysTimbre,
      pluckDamp,
      melodyDensity,
      melodyRegister,
      melodyInstrument,
      melodyTransposeSemis: 0,
      melodyRhythm,
      melodyMotif,
      motifShiftChance,
      toneCutoff,
      vinylLevel,
      reverbWet,
      bassCutoffHz,
      tapeSaturation,
      stereoWidth,
      crackleChancePerBar,
      wobbleDetuneCents,
      wobbleRateHz,
      progressionSequence
    };
  }

  function cycleBarsFromEvents(events) {
    const list = Array.isArray(events) ? events : [];
    const bars = list.map((e) => Number(e && e.bar)).filter((n) => Number.isFinite(n));
    if (!bars.length) return 4;
    const last = Math.max(...bars);
    return Math.max(1, Math.ceil((last + 1) / 4) * 4);
  }

  function mod(n, m) {
    return ((n % m) + m) % m;
  }

  function keyModeFromKeyString(key) {
    const s = String(key || '');
    if (/\bmajor\b/i.test(s)) return 'major';
    if (/\bminor\b/i.test(s)) return 'naturalMinor';
    // Default to minor for LoFi charts if unspecified.
    return 'naturalMinor';
  }

  function tonicPcFromKeyString(key) {
    const match = /^\s*([A-Ga-g])([#b]?)/.exec(String(key || ''));
    if (!match) return null;
    return pitchClassFromNoteName(match[1] + (match[2] || ''));
  }

  function prepareChart(chart) {
    if (!chart) return null;

    const chordEvents = Array.isArray(chart.chords) ? chart.chords.slice() : [];
    chordEvents.sort((a, b) => (Number(a && a.bar) || 0) - (Number(b && b.bar) || 0));
    const chordCycleBars = cycleBarsFromEvents(chordEvents);

    const melodyEvents = Array.isArray(chart.melody) ? chart.melody.slice() : [];
    melodyEvents.sort((a, b) => (Number(a && a.bar) || 0) - (Number(b && b.bar) || 0));
    const melodyCycleBars = cycleBarsFromEvents(melodyEvents);

    // Optional, human-readable hints. We treat these as best-effort.
    const arrangementNotes = Array.isArray(chart.arrangementNotes) ? chart.arrangementNotes.join(' | ') : '';
    const drumsNotes = (chart && chart.drums && chart.drums.notes) ? String(chart.drums.notes) : '';

    let drumsStartBar = 0;
    const startMatch = /drums\s+enter\s+bar\s+(\d+)/i.exec(arrangementNotes);
    if (startMatch && Number.isFinite(Number(startMatch[1]))) drumsStartBar = Number(startMatch[1]);

    let melodyStartBar = 0;
    const noMelodyMatch = /no\s+melody\s+in\s+first\s+(\d+)\s+bars/i.exec(arrangementNotes);
    if (noMelodyMatch && Number.isFinite(Number(noMelodyMatch[1]))) melodyStartBar = Number(noMelodyMatch[1]);

    const dropMelodyEveryMatch = /drop\s+melody\s+entirely\s+every\s+(\d+)\s+bars/i.exec(arrangementNotes);
    const dropMelodyEveryBars = dropMelodyEveryMatch ? Number(dropMelodyEveryMatch[1]) : 0;

    const removeKickInB = /B\s+section\s*:\s*remove\s+kick\s+on\s+step\s+0/i.test(arrangementNotes);

    const dropHatsInB = /drop\s+hats\s+entirely\s+in\s+B\s+section/i.test(arrangementNotes);

    let kickEveryBars = 0;
    const kickEveryMatch = /kick\s+every\s+(\d+)\s+bars/i.exec(arrangementNotes);
    if (kickEveryMatch && Number.isFinite(Number(kickEveryMatch[1]))) kickEveryBars = Number(kickEveryMatch[1]);

    const snareLagSec = /snare\s+slightly\s+behind/i.test(drumsNotes) ? 0.012 : 0;
    const hatLowpassHz = /hats\s+low-?passed/i.test(drumsNotes)
      ? 6500
      : (/hats?\s+very\s+dark/i.test(drumsNotes) ? 4200 : 0);

    const sections = Array.isArray(chart.sections) ? chart.sections : [];
    const barsTotal = (chart && chart.bars) ? Number(chart.bars) : 0;
    const sectionByBar = [];
    if (Number.isFinite(barsTotal) && barsTotal > 0 && sections.length) {
      let at = 0;
      for (let i = 0; i < sections.length; i += 1) {
        const s = sections[i];
        const len = Number(s && s.bars);
        const name = (s && s.name) ? String(s.name) : '';
        const nBars = Number.isFinite(len) ? len : 0;
        for (let b = 0; b < nBars && at < barsTotal; b += 1) {
          sectionByBar[at] = name;
          at += 1;
        }
      }
    }

    return {
      chordEvents,
      chordCycleBars,
      melodyEvents,
      melodyCycleBars,
      drums: (chart && chart.drums) || {},
      bass: (chart && chart.bass) || {},
      mix: (chart && chart.mix) || {},
      arrangement: {
        drumsStartBar,
        melodyStartBar,
        dropMelodyEveryBars,
        dropHatsInB,
        kickEveryBars,
        removeKickOnZeroInB: removeKickInB,
        snareLagSec,
        hatLowpassHz,
        sectionByBar
      }
    };
  }

  function chordSymbolForBar(playback, barInSong) {
    if (!playback || !playback.chordEvents || !playback.chordEvents.length) return null;
    const cycleBar = mod(barInSong, playback.chordCycleBars || 4);

    let last = null;
    for (let i = 0; i < playback.chordEvents.length; i += 1) {
      const ev = playback.chordEvents[i];
      const b = Number(ev && ev.bar);
      if (!Number.isFinite(b)) continue;
      if (b === cycleBar) return ev.symbol;
      if (b <= cycleBar) last = ev;
      if (b > cycleBar) break;
    }
    if (last) return last.symbol;
    return playback.chordEvents[playback.chordEvents.length - 1].symbol;
  }

  function melodyEventForBar(playback, barInSong) {
    if (!playback || !playback.melodyEvents || !playback.melodyEvents.length) return null;
    const cycleBar = mod(barInSong, playback.melodyCycleBars || 4);
    for (let i = 0; i < playback.melodyEvents.length; i += 1) {
      const ev = playback.melodyEvents[i];
      if (Number(ev && ev.bar) === cycleBar) return ev;
    }
    return null;
  }

  function chordBarsUntilNextChange(playback, barInSong) {
    if (!playback || !playback.chordEvents || !playback.chordEvents.length) return 1;
    const cycleBars = playback.chordCycleBars || 4;
    const cycleBar = mod(barInSong, cycleBars);

    const events = playback.chordEvents;
    let currentIndex = -1;
    for (let i = 0; i < events.length; i += 1) {
      const b = Number(events[i] && events[i].bar);
      if (!Number.isFinite(b)) continue;
      if (b === cycleBar) {
        currentIndex = i;
        break;
      }
    }
    if (currentIndex === -1) {
      // Not a change-bar; fall back to 1 bar.
      return 1;
    }

    let nextBar = null;
    for (let j = currentIndex + 1; j < events.length; j += 1) {
      const b = Number(events[j] && events[j].bar);
      if (Number.isFinite(b) && b > cycleBar) {
        nextBar = b;
        break;
      }
    }
    if (nextBar == null) {
      const first = Number(events[0] && events[0].bar);
      nextBar = (Number.isFinite(first) ? first : 0) + cycleBars;
    }

    return Math.max(1, nextBar - cycleBar);
  }

  function melodyMidiForChartEvent({ mel, barInSong, idx, rBar, keyRootMidi, chartPlayback }) {
    if (!mel) return null;
    const mode = (mel && mel.mode) ? String(mel.mode) : '';

    // Default: explicit note names.
    if (!mode) {
      const notesArr = Array.isArray(mel.notes) ? mel.notes : [];
      const noteName = notesArr.length ? notesArr[idx % notesArr.length] : null;
      const midiFromName = noteNameToMidi(noteName, 5);
      return Number.isFinite(midiFromName) ? midiFromName : null;
    }

    const tonicPc = tonicPcFromKeyString(currentSong && currentSong.key);
    const octave = (typeof mel.octave === 'number' && Number.isFinite(mel.octave)) ? mel.octave : null;
    const tonicMidi = (tonicPc != null)
      ? (octave != null ? ((octave + 1) * 12 + tonicPc) : keyRootMidi)
      : keyRootMidi;

    if (mode === 'scale') {
      const scaleMode = keyModeFromKeyString(currentSong && currentSong.key);
      const scale = scaleStepsForMode(scaleMode);
      if (!scale.length) return null;

      const shift = rBar ? Math.floor(rBar() * scale.length) : 0;
      let semis = scale[(idx + shift) % scale.length];
      if (semis === 0 && scale.length > 1) semis = scale[(idx + shift + 1) % scale.length];
      let midi = tonicMidi + 24 + semis;
      while (midi > 92) midi -= 12;
      while (midi < 52) midi += 12;
      return midi;
    }

    const symbol = chordSymbolForBar(chartPlayback, barInSong);
    const parsed = parseChordSymbol(symbol);
    if (mode === 'chordTones') {
      if (!parsed) return null;

      // Use a stable root near the key center, then place melody above.
      const rootMidiNearKey = nearestMidiForPitchClass(keyRootMidi, parsed.rootPc);
      const midiRoot = (octave != null)
        ? ((octave + 1) * 12 + parsed.rootPc)
        : (rootMidiNearKey + 12);

      // Prefer chord tones for the parsed quality; fall back if unknown.
      let intervals = null;
      try {
        intervals = chordIntervals(parsed.quality);
      } catch (e) {
        intervals = null;
      }
      if (!Array.isArray(intervals) || !intervals.length) intervals = [0, 3, 7, 10, 14];

      // Bias away from pure roots for a more "lofi" melodic feel.
      const pool = intervals.filter((x) => x !== 0);
      const tones = pool.length ? pool : intervals;
      const chosen = tones[idx % tones.length];

      // Small, deterministic variation: occasionally jump an octave.
      const jump = (rBar && rBar() < 0.12) ? (rBar() < 0.5 ? 12 : -12) : 0;
      let midi = midiRoot + chosen + jump;

      // Keep in a comfortable range.
      while (midi > 92) midi -= 12;
      while (midi < 52) midi += 12;
      return midi;
    }

    if (mode === 'motif') {
      // A repeating phrase that adapts to the current chord.
      const cached = melodyMotifCache.get(mel);
      let motifIntervals = cached;
      if (!Array.isArray(motifIntervals) || !motifIntervals.length) {
        const salt = (0xBEEF0000 ^ (Number(mel && mel.bar) || 0) ^ hashStringToSeed(String(mel && mel.instrument || '')) ) >>> 0;
        const r = mulberry32(((songSeed(currentSong) ^ salt) >>> 0) || 1);
        const len = clamp(Number(mel && mel.motifLength) || 4, 2, 8);

        let intervals = null;
        if (parsed) {
          try {
            intervals = chordIntervals(parsed.quality);
          } catch (e) {
            intervals = null;
          }
        }
        if (!Array.isArray(intervals) || !intervals.length) intervals = [0, 3, 7, 10, 14];
        const pool = intervals.filter((x) => x !== 0);
        const tones = pool.length ? pool : intervals;

        motifIntervals = [];
        for (let i = 0; i < len; i += 1) {
          let candidate = pick(r, tones);
          if (i > 0 && candidate === motifIntervals[i - 1] && tones.length > 1) {
            candidate = tones[(tones.indexOf(candidate) + 1) % tones.length];
          }
          motifIntervals.push(candidate);
        }

        melodyMotifCache.set(mel, motifIntervals);
      }

      // Base pitch follows chord root when available, otherwise tonic.
      const basePc = parsed ? parsed.rootPc : tonicPc;
      if (basePc == null) return null;
      const baseMidiNearKey = nearestMidiForPitchClass(keyRootMidi, basePc);
      const midiRoot = (octave != null)
        ? ((octave + 1) * 12 + basePc)
        : (baseMidiNearKey + 12);

      const motifIndex = (Math.floor(barInSong / 2) + idx) % motifIntervals.length;
      const semis = motifIntervals[motifIndex];
      let midi = midiRoot + semis;
      while (midi > 92) midi -= 12;
      while (midi < 52) midi += 12;
      return midi;
    }

    // Fallback: explicit notes.
    const notesArr = Array.isArray(mel.notes) ? mel.notes : [];
    const noteName = notesArr.length ? notesArr[idx % notesArr.length] : null;
    const midiFromName = noteNameToMidi(noteName, 5);
    return Number.isFinite(midiFromName) ? midiFromName : null;
  }

  function createChartProfile(chart, songObj) {
    const base = createSongProfile(songObj);
    const mix = (chart && chart.mix) || {};
    const drums = (chart && chart.drums) || {};
    const bass = (chart && chart.bass) || {};

    const kitRaw = drums.kit || base.drumKit;
    const kit = kitRaw === 'boombap-dust' ? 'dusty' : (kitRaw === 'soft-boombap' ? 'soft' : kitRaw);
    const bassCutoffHz = (typeof bass.cutoffHz === 'number') ? bass.cutoffHz : base.bassCutoffHz;

    const tapeSaturation = (typeof mix.tapeSaturation === 'number') ? mix.tapeSaturation : base.tapeSaturation;
    const stereoWidth = (typeof mix.stereoWidth === 'number') ? mix.stereoWidth : base.stereoWidth;

    // Darker, more "muffled" default for chart-authored songs.
    const highShelfFreqHz = (typeof mix.highShelfFreqHz === 'number') ? mix.highShelfFreqHz : 4500;
    const highShelfGainDb = (typeof mix.highShelfGainDb === 'number') ? mix.highShelfGainDb : -5.5;

    // Lower melody register by default for chart songs.
    const melodyTransposeSemis = (typeof mix.melodyTransposeSemis === 'number') ? mix.melodyTransposeSemis : -12;

    return {
      ...base,
      bars: (chart && chart.bars) || base.bars,
      swing: (typeof chart.swing === 'number') ? chart.swing : base.swing,
      drumKit: kit,
      kickSteps: Array.isArray(drums.kick) ? drums.kick : base.kickSteps,
      hatOpenChance: (drums.hat && typeof drums.hat.openChance === 'number') ? drums.hat.openChance : base.hatOpenChance,
      snareGhostSteps: (drums.ghostSnare && Array.isArray(drums.ghostSnare.steps)) ? drums.ghostSnare.steps : base.snareGhostSteps,
      snareGhostChance: (drums.ghostSnare && typeof drums.ghostSnare.chance === 'number') ? drums.ghostSnare.chance : base.snareGhostChance,
      reverbWet: (typeof mix.reverbWet === 'number') ? mix.reverbWet : base.reverbWet,
      vinylLevel: (typeof mix.vinyl === 'number') ? mix.vinyl : base.vinylLevel,
      toneCutoff: (typeof mix.toneCutoff === 'number') ? mix.toneCutoff : base.toneCutoff,
      bassCutoffHz,
      tapeSaturation,
      stereoWidth,
      highShelfFreqHz,
      highShelfGainDb,
      melodyTransposeSemis
    };
  }

  function ensureContext() {
    if (context) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) throw new Error('Web Audio API not supported');

    context = new Ctx();

    master = context.createGain();
    master.gain.value = state.volume;

    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -22;
    compressor.knee.value = 20;
    compressor.ratio.value = 3;
    compressor.attack.value = 0.005;
    compressor.release.value = 0.25;

    toneNode = context.createBiquadFilter();
    toneNode.type = 'lowpass';
    toneNode.frequency.value = (profile && profile.toneCutoff) ? profile.toneCutoff : 14000;
    toneNode.Q.value = 0.4;

    // Gentle high-shelf attenuation for a more "muffled" LoFi vibe.
    muffleShelfNode = context.createBiquadFilter();
    muffleShelfNode.type = 'highshelf';
    muffleShelfNode.frequency.value = (profile && typeof profile.highShelfFreqHz === 'number') ? profile.highShelfFreqHz : 4500;
    muffleShelfNode.gain.value = (profile && typeof profile.highShelfGainDb === 'number') ? profile.highShelfGainDb : -5.5;

    saturationNode = context.createWaveShaper();
    saturationNode.oversample = '2x';

    convolverNode = context.createConvolver();
    convolverNode.buffer = createImpulseResponse(context, 1.6, 2.5, rng);

    wetGainNode = context.createGain();
    wetGainNode.gain.value = (profile && profile.reverbWet) ? profile.reverbWet : 0.18;

    const dry = context.createGain();
    dry.gain.value = 0.9;

    // Routing: master -> compressor -> saturation -> tone -> split(dry/wet) -> destination
    master.connect(compressor);
    compressor.connect(saturationNode);
    saturationNode.connect(toneNode);
    toneNode.connect(muffleShelfNode);
    muffleShelfNode.connect(dry);
    muffleShelfNode.connect(convolverNode);
    convolverNode.connect(wetGainNode);
    const sum = context.createGain();
    dry.connect(sum);

    // Optional stereo widening on the wet path (Haas delay on right channel).
    const wetWidthSplitter = context.createChannelSplitter(2);
    const wetWidthMerger = context.createChannelMerger(2);

    const wetLeftGain = context.createGain();
    wetLeftGain.gain.value = 1;

    wetWidthDelayNode = context.createDelay(0.03);
    wetWidthDelayNode.delayTime.value = 0;
    const wetRightGain = context.createGain();
    wetRightGain.gain.value = 0.98;

    wetGainNode.connect(wetWidthSplitter);
    wetWidthSplitter.connect(wetLeftGain, 0);
    wetLeftGain.connect(wetWidthMerger, 0, 0);

    wetWidthSplitter.connect(wetWidthDelayNode, 1);
    wetWidthDelayNode.connect(wetRightGain);
    wetRightGain.connect(wetWidthMerger, 0, 1);

    wetWidthMerger.connect(sum);

    sum.connect(context.destination);

    // Vinyl noise bed (very low volume)
    const noise = context.createBufferSource();
    noise.buffer = createNoiseBuffer(context, 2.0, rng);
    noise.loop = true;

    const noiseFilter = context.createBiquadFilter();
    noiseFilter.type = 'highpass';
    noiseFilter.frequency.value = 2200;

    const noiseLP = context.createBiquadFilter();
    noiseLP.type = 'lowpass';
    noiseLP.frequency.value = 8200;

    noiseGainNode = context.createGain();
    noiseGainNode.gain.value = (profile && profile.vinylLevel) ? profile.vinylLevel : 0.012;

    noise.connect(noiseFilter);
    noiseFilter.connect(noiseLP);
    noiseLP.connect(noiseGainNode);
    noiseGainNode.connect(master);

    noise.start();

    // Apply saturation + width initial settings.
    const sat = (profile && typeof profile.tapeSaturation === 'number') ? profile.tapeSaturation : 0;
    const drive = 1 + clamp(sat, 0, 1) * 12;
    const n = 1024;
    const curve = new Float32Array(n);
    const norm = Math.tanh(drive);
    for (let i = 0; i < n; i += 1) {
      const x = (i / (n - 1)) * 2 - 1;
      curve[i] = Math.tanh(drive * x) / (norm || 1);
    }
    saturationNode.curve = curve;

    const width = (profile && typeof profile.stereoWidth === 'number') ? profile.stereoWidth : 0;
    wetWidthDelayNode.delayTime.value = clamp(width, 0, 1) * 0.015;
  }

  function songToKeyRootMidi(songObj) {
    // Parse key strings like "D minor", "Eb major".
    // Keep tonic in a comfortable register near A3.
    const key = (songObj && songObj.key) ? String(songObj.key) : 'A minor';
    const match = /^\s*([A-Ga-g])([#b]?)/.exec(key);
    if (!match) return 57;
    const tonicPc = pitchClassFromNoteName(match[1] + (match[2] || ''));
    if (tonicPc == null) return 57;
    return nearestMidiForPitchClass(57, tonicPc);
  }

  function recomputeDuration() {
    const bars = (profile && profile.bars) || (currentSong && currentSong.lengthBars) || 64;
    const secondsPerBeat = 60 / state.bpm;
    const beatsPerBar = 4;
    state.durationSec = bars * beatsPerBar * secondsPerBeat;
  }

  function scheduleKick(time, velocity) {
    const osc = context.createOscillator();
    const gain = context.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(140, time);
    osc.frequency.exponentialRampToValueAtTime(55, time + 0.06);

    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(0.9 * velocity, time + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.12);

    osc.connect(gain);
    gain.connect(master);

    osc.start(time);
    osc.stop(time + 0.14);
  }

  function scheduleKickAlt(time, velocity) {
    // Softer, more "thumpy" kick with a tiny click.
    const osc = context.createOscillator();
    const click = context.createBufferSource();
    click.buffer = createNoiseBuffer(context, 0.02, rng);

    const clickHP = context.createBiquadFilter();
    clickHP.type = 'highpass';
    clickHP.frequency.value = 3500;

    const clickGain = context.createGain();
    clickGain.gain.setValueAtTime(0.0001, time);
    clickGain.gain.exponentialRampToValueAtTime(0.08 * velocity, time + 0.001);
    clickGain.gain.exponentialRampToValueAtTime(0.0001, time + 0.02);

    osc.type = 'sine';
    osc.frequency.setValueAtTime(110, time);
    osc.frequency.exponentialRampToValueAtTime(45, time + 0.08);

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(0.75 * velocity, time + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.18);

    click.connect(clickHP);
    clickHP.connect(clickGain);
    clickGain.connect(master);

    osc.connect(gain);
    gain.connect(master);

    click.start(time);
    click.stop(time + 0.03);
    osc.start(time);
    osc.stop(time + 0.22);
  }

  function scheduleHat(time, velocity, { open, lowpassHz } = {}) {
    const src = context.createBufferSource();
    src.buffer = createNoiseBuffer(context, open ? 0.22 : 0.12, rng);

    const hp = context.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = open ? 5200 : 7000;

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime((open ? 0.18 : 0.22) * velocity, time + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + (open ? 0.14 : 0.06));

    src.connect(hp);
    if (Number.isFinite(lowpassHz) && lowpassHz > 0) {
      const lp = context.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = lowpassHz;
      lp.Q.value = 0.5;
      hp.connect(lp);
      lp.connect(gain);
    } else {
      hp.connect(gain);
    }
    gain.connect(master);

    src.start(time);
    src.stop(time + (open ? 0.18 : 0.08));
  }

  function scheduleSnare(time, velocity) {
    const noise = context.createBufferSource();
    noise.buffer = createNoiseBuffer(context, 0.25, rng);

    const bp = context.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1800;
    bp.Q.value = 0.8;

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(0.45 * velocity, time + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.16);

    noise.connect(bp);
    bp.connect(gain);
    gain.connect(master);

    noise.start(time);
    noise.stop(time + 0.18);

    // Tiny tonal body
    const osc = context.createOscillator();
    const og = context.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(220, time);
    og.gain.setValueAtTime(0.0001, time);
    og.gain.exponentialRampToValueAtTime(0.12 * velocity, time + 0.003);
    og.gain.exponentialRampToValueAtTime(0.0001, time + 0.08);
    osc.connect(og);
    og.connect(master);
    osc.start(time);
    osc.stop(time + 0.1);
  }

  function scheduleClap(time, velocity) {
    // Dusty clap: layered noise bursts.
    const bursts = 3;
    for (let i = 0; i < bursts; i += 1) {
      const t = time + i * 0.012;
      const noise = context.createBufferSource();
      noise.buffer = createNoiseBuffer(context, 0.08, rng);

      const bp = context.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 1600 + rng() * 700;
      bp.Q.value = 0.7;

      const gain = context.createGain();
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime((0.22 * velocity) / (i + 1), t + 0.002);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);

      noise.connect(bp);
      bp.connect(gain);
      gain.connect(master);

      noise.start(t);
      noise.stop(t + 0.07);
    }
  }

  function scheduleCrackle(time, velocity) {
    const noise = context.createBufferSource();
    noise.buffer = createNoiseBuffer(context, 0.03, rng);
    const hp = context.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 5000;
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(0.03 * velocity, time + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.018);
    noise.connect(hp);
    hp.connect(gain);
    gain.connect(master);
    noise.start(time);
    noise.stop(time + 0.025);
  }

  function scheduleChord(time, rootMidi, quality, lengthSec) {
    const intervals = chordIntervals(quality);
    const voices = Math.min(intervals.length, pick(rng, [3, 4, 4, 5]));

    for (let i = 0; i < voices; i += 1) {
      const interval = intervals[i % intervals.length];
      // Spread voicings upward for a softer, "jazzy pad" feel.
      const midi = rootMidi + interval + (i >= 3 ? 12 : 0) + (i >= 4 ? 12 : 0);

      const osc = context.createOscillator();
      const gain = context.createGain();
      const lp = context.createBiquadFilter();

      osc.type = (profile && profile.chordWave) ? profile.chordWave : pick(rng, ['triangle', 'sine']);
      osc.frequency.value = midiToHz(midi);
      const baseDetune = (profile && profile.chordDetuneCents) ? profile.chordDetuneCents : 6;
      const wobble = (profile && profile.wobbleDetuneCents) ? profile.wobbleDetuneCents : 0;
      const d1 = (rng() * 2 - 1) * baseDetune;
      const d2 = d1 + (rng() * 2 - 1) * wobble;
      osc.detune.setValueAtTime(d1, time);
      osc.detune.linearRampToValueAtTime(d2, time + lengthSec);

      lp.type = 'lowpass';
      const lpBase = (profile && profile.chordLPBase) ? profile.chordLPBase : 1200;
      const lpRng = (profile && profile.chordLPRng) ? profile.chordLPRng : 1800;
      lp.frequency.value = lpBase + rng() * lpRng;
      lp.Q.value = 0.5;

      const attack = 0.012 + rng() * 0.03;
      const release = 0.12 + rng() * 0.22;
      const sustainBase = (profile && profile.chordGainBase) ? profile.chordGainBase : 0.14;
      const sustain = sustainBase + rng() * 0.08;

      gain.gain.setValueAtTime(0.0001, time);
      gain.gain.exponentialRampToValueAtTime(sustain, time + attack);
      gain.gain.exponentialRampToValueAtTime(0.0001, time + Math.max(attack + 0.02, lengthSec - release));

      osc.connect(lp);
      lp.connect(gain);
      gain.connect(master);

      osc.start(time);
      osc.stop(time + lengthSec);
    }
  }

  function scheduleFMKeys(time, midi, lengthSec, velocity) {
    // FM-ish electric keys (Rhodes-ish): sine carrier with sine modulator.
    const freq = midiToHz(midi);
    const carrier = context.createOscillator();
    const mod = context.createOscillator();
    const modGain = context.createGain();
    const amp = context.createGain();
    const lp = context.createBiquadFilter();

    carrier.type = 'sine';
    carrier.frequency.value = freq;

    mod.type = 'sine';
    mod.frequency.value = freq * pick(rng, [1.5, 2, 3]);

    const timbre = (profile && profile.keysTimbre) ? profile.keysTimbre : 'rhodes';
    const index = timbre === 'bell' ? 1.8 : (timbre === 'tine' ? 1.2 : 0.9);
    modGain.gain.value = freq * index * 0.08;

    mod.connect(modGain);
    modGain.connect(carrier.frequency);

    lp.type = 'lowpass';
    lp.frequency.value = 1600 + rng() * 2000;
    lp.Q.value = 0.6;

    const a = 0.008 + rng() * 0.012;
    const r = 0.18 + rng() * 0.35;
    const s = 0.12 + rng() * 0.12;

    amp.gain.setValueAtTime(0.0001, time);
    amp.gain.exponentialRampToValueAtTime(s * velocity, time + a);
    amp.gain.exponentialRampToValueAtTime(0.0001, time + Math.max(a + 0.02, lengthSec - r));

    carrier.connect(lp);
    lp.connect(amp);
    amp.connect(master);

    mod.start(time);
    carrier.start(time);
    mod.stop(time + lengthSec + 0.02);
    carrier.stop(time + lengthSec + 0.02);
  }

  function scheduleMutedKeys(time, midi, lengthSec, velocity) {
    // Darker, quieter keys for background phrases.
    const freq = midiToHz(midi);
    const carrier = context.createOscillator();
    const mod = context.createOscillator();
    const modGain = context.createGain();
    const amp = context.createGain();
    const lp = context.createBiquadFilter();

    carrier.type = 'sine';
    carrier.frequency.value = freq;

    mod.type = 'sine';
    mod.frequency.value = freq * pick(rng, [1.5, 2]);
    modGain.gain.value = freq * 0.06;

    mod.connect(modGain);
    modGain.connect(carrier.frequency);

    lp.type = 'lowpass';
    lp.frequency.value = 900 + rng() * 900;
    lp.Q.value = 0.7;

    const a = 0.01 + rng() * 0.02;
    const r = 0.22 + rng() * 0.4;
    const s = 0.08 + rng() * 0.08;

    amp.gain.setValueAtTime(0.0001, time);
    amp.gain.exponentialRampToValueAtTime(s * velocity, time + a);
    amp.gain.exponentialRampToValueAtTime(0.0001, time + Math.max(a + 0.02, lengthSec - r));

    carrier.connect(lp);
    lp.connect(amp);
    amp.connect(master);

    mod.start(time);
    carrier.start(time);
    mod.stop(time + lengthSec + 0.02);
    carrier.stop(time + lengthSec + 0.02);
  }

  function scheduleTapeKeys(time, midi, lengthSec, velocity) {
    // Slightly warbly, saturated keys (still subtle).
    const freq = midiToHz(midi);
    const carrier = context.createOscillator();
    const amp = context.createGain();
    const lp = context.createBiquadFilter();

    carrier.type = 'triangle';
    carrier.frequency.value = freq;

    // Gentle wow/flutter via detune.
    const detuneLfo = context.createOscillator();
    const detuneGain = context.createGain();
    detuneLfo.type = 'sine';
    detuneLfo.frequency.value = 0.4 + rng() * 0.35;
    detuneGain.gain.value = 6 + rng() * 8;
    detuneLfo.connect(detuneGain);
    detuneGain.connect(carrier.detune);

    lp.type = 'lowpass';
    lp.frequency.value = 850 + rng() * 900;
    lp.Q.value = 0.65;

    const a = 0.02 + rng() * 0.03;
    const r = 0.35 + rng() * 0.55;
    const s = 0.07 + rng() * 0.08;

    amp.gain.setValueAtTime(0.0001, time);
    amp.gain.exponentialRampToValueAtTime(s * velocity, time + a);
    amp.gain.exponentialRampToValueAtTime(0.0001, time + Math.max(a + 0.02, lengthSec - r));

    carrier.connect(lp);
    lp.connect(amp);
    amp.connect(master);

    detuneLfo.start(time);
    carrier.start(time);
    detuneLfo.stop(time + lengthSec + 0.05);
    carrier.stop(time + lengthSec + 0.05);
  }

  function schedulePluck(time, midi, lengthSec, velocity) {
    // Simple Karplus-Strong-ish pluck.
    const freq = midiToHz(midi);
    const delayTime = 1 / Math.max(60, Math.min(1200, freq));

    const src = context.createBufferSource();
    src.buffer = createNoiseBuffer(context, 0.05, rng);

    const delay = context.createDelay();
    delay.delayTime.value = delayTime;

    const feedback = context.createGain();
    const damp = context.createBiquadFilter();
    damp.type = 'lowpass';
    const dampAmt = (profile && profile.pluckDamp) ? profile.pluckDamp : 0.5;
    damp.frequency.value = 900 + (1 - dampAmt) * 2500;
    feedback.gain.value = 0.55 + (1 - dampAmt) * 0.25;

    const amp = context.createGain();
    amp.gain.setValueAtTime(0.0001, time);
    amp.gain.exponentialRampToValueAtTime(0.18 * velocity, time + 0.004);
    amp.gain.exponentialRampToValueAtTime(0.0001, time + lengthSec);

    src.connect(delay);
    delay.connect(damp);
    damp.connect(feedback);
    feedback.connect(delay);
    damp.connect(amp);
    amp.connect(master);

    src.start(time);
    src.stop(time + 0.06);

    // Let the feedback ring, but clamp with stop.
    const stopAt = time + Math.min(1.6, Math.max(0.35, lengthSec));
    window.setTimeout(() => {
      try {
        feedback.gain.value = 0;
      } catch (e) {
        // ignore
      }
    }, Math.max(0, (stopAt - (context.currentTime || 0)) * 1000));
  }

  function scheduleSoftLead(time, midi, lengthSec, velocity) {
    // Very gentle lead (filtered triangle) for melodic lines.
    const osc = context.createOscillator();
    const gain = context.createGain();
    const lp = context.createBiquadFilter();

    osc.type = 'triangle';
    osc.frequency.value = midiToHz(midi);
    const wobble = (profile && profile.wobbleDetuneCents) ? profile.wobbleDetuneCents : 1.2;
    osc.detune.value = (rng() * 2 - 1) * wobble;

    lp.type = 'lowpass';
    lp.frequency.value = 1200 + rng() * 1600;
    lp.Q.value = 0.5;

    const a = 0.01 + rng() * 0.02;
    const r = 0.12 + rng() * 0.25;
    const s = 0.10 + rng() * 0.10;

    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(s * velocity, time + a);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + Math.max(a + 0.02, lengthSec - r));

    osc.connect(lp);
    lp.connect(gain);
    gain.connect(master);

    osc.start(time);
    osc.stop(time + lengthSec + 0.02);
  }

  let lastHeldBassMidi = null;

  function scheduleBassHeld(time, midi, lengthSec, velocity = 0.55, glideSec = 0.09) {
    const osc = context.createOscillator();
    const gain = context.createGain();
    const lp = context.createBiquadFilter();

    osc.type = 'sine';
    const fromMidi = (lastHeldBassMidi == null) ? midi : lastHeldBassMidi;
    osc.frequency.setValueAtTime(midiToHz(fromMidi), time);
    osc.frequency.exponentialRampToValueAtTime(midiToHz(midi), time + Math.max(0.01, glideSec));
    lastHeldBassMidi = midi;

    lp.type = 'lowpass';
    lp.frequency.value = (profile && profile.bassCutoffHz) ? profile.bassCutoffHz : 180;
    lp.Q.value = 0.7;

    // Very soft attack, long sustain.
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(0.16 * velocity, time + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + Math.max(0.06, lengthSec));

    osc.connect(lp);
    lp.connect(gain);
    gain.connect(master);

    osc.start(time);
    osc.stop(time + lengthSec + 0.04);
  }

  function scheduleBass(time, midi, lengthSec) {
    const osc = context.createOscillator();
    const gain = context.createGain();
    const lp = context.createBiquadFilter();

    osc.type = 'sine';
    osc.frequency.value = midiToHz(midi);
    osc.detune.value = (rng() * 2 - 1) * 4;

    lp.type = 'lowpass';
    lp.frequency.value = (profile && profile.bassCutoffHz) ? profile.bassCutoffHz : 240;
    lp.Q.value = 0.7;

    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(0.22, time + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + lengthSec);

    osc.connect(lp);
    lp.connect(gain);
    gain.connect(master);

    osc.start(time);
    osc.stop(time + lengthSec + 0.02);
  }

  chartPlayback = currentChart ? prepareChart(currentChart) : null;
  profile = currentChart ? createChartProfile(currentChart, currentSong) : createSongProfile(currentSong);
  let keyRootMidi = songToKeyRootMidi(currentSong); // A3-ish tonal center

  function setSong(nextSong, { keepPlaying } = {}) {
    currentSong = nextSong || playlist[0];
    songIndex = Math.max(0, playlist.findIndex((s) => s.id === currentSong.id));
    rng = mulberry32((currentSong && currentSong.seed) || 1);

    currentChart = (currentSong && currentSong.type === 'chart')
      ? (LOFI_CHARTS[(currentSong && (currentSong.chartId || currentSong.id))] || null)
      : null;
    chartPlayback = currentChart ? prepareChart(currentChart) : null;
    profile = currentChart ? createChartProfile(currentChart, currentSong) : createSongProfile(currentSong);

    state.songName = currentSong.name;
    state.bpm = currentSong.bpm || state.bpm;
    state.key = currentSong.key || state.key;
    state.songIndex = songIndex;

    keyRootMidi = songToKeyRootMidi(currentSong);
    recomputeDuration();

    if (toneNode && profile && profile.toneCutoff) {
      toneNode.frequency.value = profile.toneCutoff;
    }
    if (muffleShelfNode && profile && typeof profile.highShelfFreqHz === 'number') {
      muffleShelfNode.frequency.value = profile.highShelfFreqHz;
    }
    if (muffleShelfNode && profile && typeof profile.highShelfGainDb === 'number') {
      muffleShelfNode.gain.value = profile.highShelfGainDb;
    }
    if (wetGainNode && profile && typeof profile.reverbWet === 'number') {
      wetGainNode.gain.value = clamp(profile.reverbWet, 0, 1);
    }
    if (noiseGainNode && profile && profile.vinylLevel) {
      noiseGainNode.gain.value = profile.vinylLevel;
    }
    if (saturationNode && profile && typeof profile.tapeSaturation === 'number') {
      const sat = clamp(profile.tapeSaturation, 0, 1);
      const drive = 1 + sat * 12;
      const n = 1024;
      const curve = new Float32Array(n);
      const norm = Math.tanh(drive);
      for (let i = 0; i < n; i += 1) {
        const x = (i / (n - 1)) * 2 - 1;
        curve[i] = Math.tanh(drive * x) / (norm || 1);
      }
      saturationNode.curve = curve;
    }
    if (wetWidthDelayNode && profile && typeof profile.stereoWidth === 'number') {
      wetWidthDelayNode.delayTime.value = clamp(profile.stereoWidth, 0, 1) * 0.015;
    }
    if (convolverNode) {
      // Refresh the reverb texture per song.
      const rr = profileRng(currentSong, 0x1234567);
      convolverNode.buffer = createImpulseResponse(context, 1.6, 2.5, rr);
    }

    // Reset transport
    transportOffsetSec = 0;
    startTimeSec = context ? context.currentTime : 0;
    started = false;
    step = 0;
    nextNoteTime = context ? context.currentTime + 0.05 : 0;
    lastAutoAdvanceAt = -1;

    emit();

    if (keepPlaying && state.playing) {
      // Scheduler will pick up from reset values.
      started = true;
    }
  }

  recomputeDuration();

  function scheduleStep(time) {
    const secondsPerBeat = 60 / state.bpm;
    const stepPerBeat = 4; // 16th notes
    const stepDur = secondsPerBeat / stepPerBeat;

    const stepsPerBar = 16;
    const songBars = (profile && profile.bars) ? profile.bars : ((currentSong && currentSong.lengthBars) || 64);
    const absoluteBar = Math.floor(step / stepsPerBar);
    const barInSong = ((absoluteBar % songBars) + songBars) % songBars;
    const chunkIndex = Math.floor(barInSong / 4);
    const barInChunk = barInSong % 4;
    const posInBar = step % stepsPerBar;

    // Swing: delay off 16ths a bit
    const swing = (profile && profile.swing) ? profile.swing : 0.06;
    const swungTime = time + (posInBar % 2 === 1 ? stepDur * swing : 0);

    const rBar = profileRng(currentSong, (0x9000000 + barInSong) >>> 0);

    // Occasional vinyl crackle.
    if (posInBar === 0 && profile && rBar() < profile.crackleChancePerBar) {
      scheduleCrackle(swungTime + rBar() * stepDur * 8, 1.0);
    }

    // Chart playback: if selected song is a JSON chart, drive events from it.
    if (chartPlayback && currentChart) {
      const drums = chartPlayback.drums || {};
      const arrangement = chartPlayback.arrangement || {};
      const sectionByBar = Array.isArray(arrangement.sectionByBar) ? arrangement.sectionByBar : [];
      const sectionName = sectionByBar[barInSong] || '';

      const kit = (profile && profile.drumKit) || 'soft';

      const drumsActive = barInSong >= (arrangement.drumsStartBar || 0);
      const snareLagSec = arrangement.snareLagSec || 0;
      const hatLowpassHz = arrangement.hatLowpassHz || 0;
      const kickEveryBars = arrangement.kickEveryBars || 0;

      const dropHatsInB = !!arrangement.dropHatsInB;

      const removeKickOnZeroInB = !!arrangement.removeKickOnZeroInB;

      const kickSteps = Array.isArray(drums.kick) ? drums.kick : ((profile && profile.kickSteps) ? profile.kickSteps : [0, 8]);
      const kickBlocked = removeKickOnZeroInB && /^B/i.test(String(sectionName || '')) && posInBar === 0;
      const kickAllowedThisBar = !(kickEveryBars > 1) || (barInSong % kickEveryBars) === 0;
      if (drumsActive && kickAllowedThisBar && !kickBlocked && kickSteps.includes(posInBar)) {
        const base = posInBar === 0 ? 1.0 : 0.55;
        if (kit === 'crisp') scheduleKick(swungTime, base + rng() * 0.15);
        else scheduleKickAlt(swungTime, base + rng() * 0.15);
      }

      const snareSteps = Array.isArray(drums.snare) ? drums.snare : [4, 12];
      if (drumsActive && snareSteps.includes(posInBar)) {
        if (kit === 'dusty') scheduleClap(swungTime, 0.85 + rng() * 0.10);
        else scheduleSnare(swungTime + snareLagSec, 0.88 + rng() * 0.10);
      }

      const ghost = drums.ghostSnare || {};
      const ghostSteps = Array.isArray(ghost.steps) ? ghost.steps : ((profile && profile.snareGhostSteps) ? profile.snareGhostSteps : []);
      const ghostChance = (typeof ghost.chance === 'number') ? ghost.chance : ((profile && profile.snareGhostChance) ? profile.snareGhostChance : 0.25);
      const ghostVel = (typeof ghost.velocity === 'number') ? ghost.velocity : (0.22 + rng() * 0.18);
      if (drumsActive && ghostSteps.includes(posInBar) && rBar() < ghostChance) {
        scheduleSnare(swungTime + snareLagSec, ghostVel);
      }

      const hatCfg = drums.hat || {};
      const hatPattern = (typeof hatCfg.pattern === 'string') ? hatCfg.pattern : '';
      const hatEvery = (typeof hatCfg.every === 'number') ? hatCfg.every : 2;
      const hatDensity = (typeof hatCfg.density === 'number') ? hatCfg.density : 0.9;
      const openChance = (typeof hatCfg.openChance === 'number') ? hatCfg.openChance : ((profile && profile.hatOpenChance) ? profile.hatOpenChance : 0.08);
      const velRange = Array.isArray(hatCfg.velocityRange) ? hatCfg.velocityRange : null;
      const velMin = velRange && Number.isFinite(velRange[0]) ? velRange[0] : null;
      const velMax = velRange && Number.isFinite(velRange[1]) ? velRange[1] : null;
      const hatVel = (velMin != null && velMax != null)
        ? clamp(velMin + (velMax - velMin) * rBar(), 0.05, 1)
        : (0.45 + rng() * 0.25);

      const open = rBar() < openChance && posInBar !== 0;
      const hatsAllowed = !dropHatsInB || !/^B/i.test(String(sectionName || ''));
      if (drumsActive && hatsAllowed && hatPattern && hatPattern.length >= 16) {
        const c = hatPattern[posInBar % 16];
        if (c && c.toLowerCase() === 'x') {
          scheduleHat(swungTime, hatVel, { open, lowpassHz: hatLowpassHz || undefined });
        }
      } else if (drumsActive && hatsAllowed && hatEvery > 0 && posInBar % hatEvery === 0) {
        if (rBar() < hatDensity) {
          scheduleHat(swungTime, hatVel, { open, lowpassHz: hatLowpassHz || undefined });
        }
      }

      // Harmony + bass: one chord symbol per bar.
      if (posInBar === 0) {
        const chordGate = (chartPlayback && chartPlayback.mix && typeof chartPlayback.mix.chordGate === 'number')
          ? clamp(chartPlayback.mix.chordGate, 0.15, 1)
          : 0.95;
        const symbol = chordSymbolForBar(chartPlayback, barInSong);
        const prevSymbol = chordSymbolForBar(chartPlayback, barInSong - 1);
        const isChange = symbol && symbol !== prevSymbol;

        if (isChange) {
          const parsed = parseChordSymbol(symbol);
          if (parsed) {
            const rootMidi = nearestMidiForPitchClass(keyRootMidi, parsed.rootPc);

            const durBars = chordBarsUntilNextChange(chartPlayback, barInSong);
            const baseLen = Math.max(secondsPerBeat * 2.0, secondsPerBeat * 4 * durBars - secondsPerBeat * 0.2);
            const chordLen = Math.max(secondsPerBeat * 0.85, baseLen * chordGate);
            scheduleChord(swungTime, rootMidi + 12, parsed.quality, chordLen);

            const bassRoot = rootMidi - 24;
            const styleRaw = (chartPlayback.bass && chartPlayback.bass.style) ? chartPlayback.bass.style : 'root-approach';
            const style = styleRaw === 'subtle-walk' ? 'subtle-walk' : styleRaw;
            if (style === 'held-notes') {
              const bassLen = Math.max(secondsPerBeat * 4, secondsPerBeat * 4 * durBars - secondsPerBeat * 0.1);
              scheduleBassHeld(swungTime, bassRoot, bassLen, 0.55, 0.12);
            } else if (style === 'root-fifth') {
              scheduleBass(swungTime, bassRoot, secondsPerBeat * 0.9);
              scheduleBass(swungTime + secondsPerBeat, bassRoot + 7, secondsPerBeat * 0.7);
              const approach = bassRoot + pick(rng, [-3, -2, 2]);
              scheduleBass(swungTime + secondsPerBeat * 2, approach, secondsPerBeat * 0.7);
            } else if (style === 'subtle-walk') {
              scheduleBass(swungTime, bassRoot, secondsPerBeat * 0.95);
              if (rBar() < 0.55) {
                scheduleBass(swungTime + secondsPerBeat * 2.5, bassRoot + pick(rng, [2, 3, 5, 7]), secondsPerBeat * 0.55);
              }
            } else if (style === 'walking') {
              scheduleBass(swungTime, bassRoot, secondsPerBeat * 0.7);
              scheduleBass(swungTime + secondsPerBeat, bassRoot + pick(rng, [2, 3, 5]), secondsPerBeat * 0.6);
              scheduleBass(swungTime + secondsPerBeat * 2, bassRoot + pick(rng, [7, 10]), secondsPerBeat * 0.6);
            } else {
              scheduleBass(swungTime, bassRoot, secondsPerBeat * 0.9);
              const approach = bassRoot + pick(rng, [-2, -3, 2, 5]);
              scheduleBass(swungTime + secondsPerBeat * 2, approach, secondsPerBeat * 0.8);
            }
          }
        }
      }

      // Melody events: authored notes per bar.
      const dropEvery = arrangement.dropMelodyEveryBars || 0;
      const dropMelodyThisBar = dropEvery > 0 && (barInSong % dropEvery) === (dropEvery - 1);
      const melodyStartBar = arrangement.melodyStartBar || 0;
      const melodyAllowed = barInSong >= melodyStartBar;
      const mel = (!melodyAllowed || dropMelodyThisBar) ? null : melodyEventForBar(chartPlayback, barInSong);
      if (mel && Array.isArray(mel.steps) && mel.steps.includes(posInBar)) {
        const stepsArr = mel.steps;
        const idx = Math.max(0, stepsArr.indexOf(posInBar));
        let midi = melodyMidiForChartEvent({
          mel,
          barInSong,
          idx,
          rBar,
          keyRootMidi,
          chartPlayback
        });
        const transpose = (profile && typeof profile.melodyTransposeSemis === 'number') ? profile.melodyTransposeSemis : 0;
        if (Number.isFinite(midi) && transpose) midi += transpose;
        while (Number.isFinite(midi) && midi > 90) midi -= 12;
        while (Number.isFinite(midi) && midi < 52) midi += 12;
        if (Number.isFinite(midi)) {
          const vel = (typeof mel.vel === 'number') ? mel.vel : 0.32;
          const lenSteps = (typeof mel.lenSteps === 'number') ? mel.lenSteps : 3;
          const len = Math.max(stepDur * 0.5, stepDur * lenSteps);
          const inst = mel.instrument || 'keys';
          if (inst === 'pluck') schedulePluck(swungTime, midi, len, vel);
          else if (inst === 'softLead') scheduleSoftLead(swungTime, midi, len, vel);
          else if (inst === 'mutedKeys') scheduleMutedKeys(swungTime, midi, len, vel);
          else if (inst === 'tapeKeys') scheduleTapeKeys(swungTime, midi, len, vel);
          else scheduleFMKeys(swungTime, midi, len, vel);
        }
      }

      step += 1;
      nextNoteTime += stepDur;
      return;
    }

    // Drums
    const kickSteps = (profile && profile.kickSteps) ? profile.kickSteps : [0, 8];
    if (kickSteps.includes(posInBar)) {
      const base = posInBar === 0 ? 1.0 : 0.55;
      const kit = profile && profile.drumKit ? profile.drumKit : 'soft';
      if (kit === 'crisp') scheduleKick(swungTime, base + rng() * 0.15);
      else scheduleKickAlt(swungTime, base + rng() * 0.15);
    }

    const feel = profile && profile.drumFeel ? profile.drumFeel : 'standard';
    const snareMain = feel === 'halftime' ? [12] : [4, 12];
    if (snareMain.includes(posInBar)) {
      const kit = profile && profile.drumKit ? profile.drumKit : 'soft';
      if (kit === 'dusty') scheduleClap(swungTime, 0.85 + rng() * 0.10);
      else scheduleSnare(swungTime, 0.88 + rng() * 0.10);
    }

    const ghostSteps = (profile && profile.snareGhostSteps) ? profile.snareGhostSteps : [];
    const ghostChance = (profile && profile.snareGhostChance) ? profile.snareGhostChance : 0.25;
    if (ghostSteps.includes(posInBar) && rBar() < ghostChance) {
      scheduleSnare(swungTime, 0.22 + rng() * 0.18);
    }

    const hatMode = (profile && profile.hatMode) ? profile.hatMode : 'eighth';
    const hatEvery = hatMode === 'sixteenth' ? 1 : (hatMode === 'sparse' ? 4 : 2);
    if (posInBar % hatEvery === 0) {
      // A tiny, deterministic skip so it doesn't get too busy.
      const density = hatMode === 'sixteenth' ? 0.82 : (hatMode === 'sparse' ? 0.55 : 0.9);
      if (rBar() < density) {
        const openChance = (profile && profile.hatOpenChance) ? profile.hatOpenChance : 0.08;
        const open = rBar() < openChance && posInBar !== 0;
        scheduleHat(swungTime, 0.45 + rng() * 0.25, { open });
      }
    }

    // Harmony: one chord per bar, held almost full bar
    if (posInBar === 0) {
      const seq = (profile && profile.progressionSequence) ? profile.progressionSequence : [[0, 5, 7, 3]];
      const prog = seq[chunkIndex % seq.length];
      const degree = prog[barInChunk];
      const root = keyRootMidi + degree;
      const quality = chordQualityForDegree(degree, profileRng(currentSong, 0xFACE000 + barInSong));
      const chordStyle = (profile && profile.chordRhythm) ? profile.chordRhythm : 'pad';
      if (chordStyle === 'arp') {
        const intervals = chordIntervals(quality);
        const density = (profile && profile.arpDensity) ? profile.arpDensity : 3;
        for (let i = 0; i < density; i += 1) {
          const note = root + 12 + intervals[i % intervals.length] + (i >= 3 ? 12 : 0);
          scheduleFMKeys(swungTime + i * secondsPerBeat * 0.5, note, secondsPerBeat * 0.45, 0.8);
        }
        // Light pad under it.
        scheduleChord(swungTime, root + 12, quality, secondsPerBeat * 3.2);
      } else if (chordStyle === 'chop') {
        // Simple "chop" by re-triggering shorter chord stabs.
        scheduleChord(swungTime, root + 12, quality, secondsPerBeat * 1.2);
        scheduleChord(swungTime + secondsPerBeat * 2, root + 12, quality, secondsPerBeat * 1.2);
      } else {
        scheduleChord(swungTime, root + 12, quality, secondsPerBeat * 3.9);
      }

      // Bass patterns (calm / hiphop-ish)
      const bassRoot = root - 24;
      const style = (profile && profile.bassStyle) ? profile.bassStyle : 'root-approach';
      if (style === 'root-fifth') {
        scheduleBass(swungTime, bassRoot, secondsPerBeat * 0.9);
        scheduleBass(swungTime + secondsPerBeat, bassRoot + 7, secondsPerBeat * 0.7);
        const approach = bassRoot + pick(rng, [-3, -2, 2]);
        scheduleBass(swungTime + secondsPerBeat * 2, approach, secondsPerBeat * 0.7);
      } else if (style === 'walking') {
        scheduleBass(swungTime, bassRoot, secondsPerBeat * 0.7);
        scheduleBass(swungTime + secondsPerBeat, bassRoot + pick(rng, [2, 3, 5]), secondsPerBeat * 0.6);
        scheduleBass(swungTime + secondsPerBeat * 2, bassRoot + pick(rng, [7, 10]), secondsPerBeat * 0.6);
      } else {
        scheduleBass(swungTime, bassRoot, secondsPerBeat * 0.9);
        const approach = bassRoot + pick(rng, [-2, -3, 2, 5]);
        scheduleBass(swungTime + secondsPerBeat * 2, approach, secondsPerBeat * 0.8);
      }
    }

    // Extra layers for uniqueness.
    if (profile && posInBar === 8) {
      const arrangement = profile.arrangement;
      const scale = scaleStepsForMode(profile.mode);
      const seq = profile.progressionSequence;
      const prog = seq[chunkIndex % seq.length];
      const degree = prog[barInChunk];
      const chordRoot = keyRootMidi + degree;

      // Keys comping (off-beat) or pluck.
      if ((arrangement === 'pad+keys' || arrangement === 'keys' || arrangement === 'pad+keys+melody') && rBar() < 0.75) {
        const note = chordRoot + 24 + pick(rng, [0, 3, 7, 10, 14]);
        scheduleFMKeys(swungTime + secondsPerBeat * 0.25, note, secondsPerBeat * 0.8, 0.55);
      }
      if ((arrangement === 'pad+pluck') && rBar() < 0.65) {
        const degreeNote = pick(rng, scale);
        schedulePluck(swungTime + secondsPerBeat * 0.25, keyRootMidi + degreeNote + 24, secondsPerBeat * 0.9, 0.75);
      }
    }

    // Melody: every song gets a calm, motif-based line.
    if (profile) {
      const rhythm = profile.melodyRhythm || [2, 6, 10, 14];
      if (rhythm.includes(posInBar)) {
        const density = profile.melodyDensity || 0.28;
        if (rBar() < density) {
          const seq = profile.progressionSequence;
          const prog = seq[chunkIndex % seq.length];
          const degree = prog[barInChunk];
          const chordRoot = keyRootMidi + degree;

          // Pick a note: mostly from motif, sometimes shift motif for variation.
          const motif = profile.melodyMotif || scaleStepsForMode(profile.mode);
          const motifShift = rBar() < (profile.motifShiftChance || 0.18);
          const motifIndex = (Math.floor(barInSong / 2) + posInBar) % motif.length;
          const degreeNote = motif[motifIndex];

          // Favor chord tones occasionally.
          const chordTones = [0, 3, 4, 7, 10, 11, 14];
          const useChordTone = rBar() < 0.35;
          const noteSemis = useChordTone ? pick(rng, chordTones) : degreeNote;

          const octave = (profile && profile.melodyRegister) ? profile.melodyRegister : 24;
          const baseMidi = (motifShift ? chordRoot : keyRootMidi) + octave + 12;
          const midi = baseMidi + noteSemis;

          const vel = 0.32 + rng() * 0.18;
          const len = secondsPerBeat * (0.45 + rng() * 0.45);
          const inst = profile.melodyInstrument || 'keys';
          if (inst === 'pluck') schedulePluck(swungTime, midi, len, vel);
          else if (inst === 'softLead') scheduleSoftLead(swungTime, midi, len, vel);
          else scheduleFMKeys(swungTime, midi, len, vel);
        }
      }
    }

    step += 1;
    nextNoteTime += stepDur;
  }

  function scheduler() {
    const scheduleAheadSec = 0.12;
    while (nextNoteTime < context.currentTime + scheduleAheadSec) {
      scheduleStep(nextNoteTime);
    }
  }

  async function start() {
    ensureContext();

    if (context.state === 'suspended') {
      await context.resume();
    }

    if (!started) {
      nextNoteTime = context.currentTime + 0.05;
      startTimeSec = context.currentTime;
      const secondsPerBeat = 60 / state.bpm;
      const stepPerBeat = 4;
      const stepDur = secondsPerBeat / stepPerBeat;
      step = Math.floor(transportOffsetSec / stepDur);
      started = true;
    }

    if (!schedulerId) {
      schedulerId = window.setInterval(scheduler, 25);
    }

    if (!uiTickId) {
      uiTickId = window.setInterval(() => {
        if (!context || !state.playing) return;
        const elapsed = context.currentTime - startTimeSec;
        const absolute = elapsed + transportOffsetSec;
        state.positionSec = ((absolute % state.durationSec) + state.durationSec) % state.durationSec;

        // Auto-advance at end of "song" loop.
        const boundary = Math.floor(absolute / state.durationSec);
        if (state.durationSec > 0 && boundary !== lastAutoAdvanceAt) {
          if (lastAutoAdvanceAt >= 0) {
            const nextIdx = (state.songIndex + 1) % playlist.length;
            setSong(playlist[nextIdx], { keepPlaying: true });
            // Keep playing from start.
            transportOffsetSec = 0;
            startTimeSec = context.currentTime;
            started = false;
          }
          lastAutoAdvanceAt = boundary;
        }

        emit();
      }, 250);
    }

    state.playing = true;
    emit();
  }

  async function stop() {
    state.playing = false;
    emit();

    if (schedulerId) {
      window.clearInterval(schedulerId);
      schedulerId = null;
    }

    if (uiTickId) {
      window.clearInterval(uiTickId);
      uiTickId = null;
    }

    if (context && context.state === 'running') {
      await context.suspend();
    }
  }

  function setVolume(volume) {
    state.volume = clamp(volume, 0, 1);
    if (master) master.gain.value = state.volume;
    emit();
  }

  async function seek(positionSec) {
    ensureContext();
    const clamped = clamp(positionSec, 0, state.durationSec || 0);
    transportOffsetSec = clamped;
    startTimeSec = context.currentTime;

    const secondsPerBeat = 60 / state.bpm;
    const stepPerBeat = 4;
    const stepDur = secondsPerBeat / stepPerBeat;
    step = Math.floor(transportOffsetSec / stepDur);
    nextNoteTime = context.currentTime + 0.05;
    started = true;

    emit();
  }

  function nextSong() {
    const nextIdx = (state.songIndex + 1) % playlist.length;
    setSong(playlist[nextIdx], { keepPlaying: true });
  }

  function prevSong() {
    const prevIdx = (state.songIndex - 1 + playlist.length) % playlist.length;
    setSong(playlist[prevIdx], { keepPlaying: true });
  }

  function selectSongById(id) {
    const idx = playlist.findIndex((s) => s.id === id);
    if (idx === -1) return;
    setSong(playlist[idx], { keepPlaying: true });
  }

  function getPlaylist() {
    return playlist.slice();
  }

  async function destroy() {
    if (schedulerId) {
      window.clearInterval(schedulerId);
      schedulerId = null;
    }
    if (uiTickId) {
      window.clearInterval(uiTickId);
      uiTickId = null;
    }
    state.playing = false;
    emit();

    if (context) {
      try {
        await context.close();
      } catch (e) {
        // ignore
      }
    }

    context = null;
    master = null;
    started = false;
  }

  function getState() {
    return { ...state };
  }

  return {
    getState,
    getPlaylist,
    start,
    stop,
    seek,
    nextSong,
    prevSong,
    selectSongById,
    setVolume,
    destroy
  };
}
